import {lazy, useState} from 'react'
import {View} from 'react-native'
// @ts-expect-error missing types
import QRCode from 'react-native-qrcode-styled'
import type ViewShot from 'react-native-view-shot'
import {type AppBskyGraphDefs, AppBskyGraphStarterpack} from '@atproto/api'
import {Trans} from '@lingui/react/macro'

import {Logo} from '#/view/icons/Logo'
import {Logotype} from '#/view/icons/Logotype'
import {atoms as a, useTheme} from '#/alf'
import {LinearGradientBackground} from '#/components/LinearGradientBackground'
import {Text} from '#/components/Typography'
import {IS_WEB} from '#/env'
import * as bsky from '#/types/bsky'
import {QR_QUIET_ZONE_MODULES, qrModuleSize} from './qrSizing'

const LazyViewShot = lazy(
  // @ts-expect-error dynamic import
  () => import('react-native-view-shot/src/index'),
)

export function QrCode({
  starterPack,
  link,
  ref,
}: {
  starterPack: AppBskyGraphDefs.StarterPackView
  link: string
  ref: React.Ref<ViewShot>
}) {
  const {record} = starterPack

  if (
    !bsky.dangerousIsType<AppBskyGraphStarterpack.Record>(
      record,
      AppBskyGraphStarterpack.isRecord,
    )
  ) {
    return null
  }

  return (
    <LazyViewShot ref={ref}>
      <LinearGradientBackground
        style={[
          {width: 300, minHeight: 390},
          a.align_center,
          a.px_sm,
          a.py_xl,
          a.rounded_sm,
          a.justify_between,
          a.gap_md,
        ]}>
        <View style={[a.gap_sm]}>
          <Text
            style={[
              a.font_semi_bold,
              a.text_3xl,
              a.text_center,
              {color: 'white'},
            ]}>
            {record.name}
          </Text>
        </View>
        <View style={[a.gap_xl, a.align_center]}>
          <Text
            style={[
              a.font_semi_bold,
              a.text_center,
              {color: 'white', fontSize: 18},
            ]}>
            <Trans>Join the conversation</Trans>
          </Text>
          <View style={[a.rounded_sm, a.overflow_hidden]}>
            <QrCodeInner link={link} />
          </View>

          <Text
            style={[
              a.flex,
              a.flex_row,
              a.align_center,
              a.font_semi_bold,
              {color: 'white', fontSize: 18, gap: 6},
            ]}>
            <Trans>
              on
              <View style={[a.flex_row, a.align_center, {gap: 6}]}>
                {/* Icon shrunk ~10% (25 → 23) and the wordmark is sized so its
                    glyph height (105 × 92/420 ≈ 23) matches the icon, sitting
                    on the same center line — the same treatment as the family
                    invite card. */}
                <Logo width={23} fill="white" />
                <Logotype width={105} fill="white" />
              </View>
            </Trans>
          </Text>
        </View>
      </LinearGradientBackground>
    </LazyViewShot>
  )
}

export function QrCodeInner({
  link,
  logoScale = 1,
  size = 225,
  plain = false,
}: {
  link: string
  /** Multiplier for the center logo size (e.g. 0.9 shrinks it 10%). */
  logoScale?: number
  /** The drawn box, in points. */
  size?: number
  /**
   * A black, unmarked code drawn at exactly `size` (e.g. a classroom's
   * invitation), instead of the decorated, branded one.
   */
  plain?: boolean
}) {
  if (plain) return <PlainQrCode link={link} size={size} />
  return <DecoratedQrCode link={link} logoScale={logoScale} size={size} />
}

function DecoratedQrCode({
  link,
  logoScale,
  size,
}: {
  link: string
  logoScale: number
  size: number
}) {
  const t = useTheme()
  const logoSize = 34 * logoScale
  const logoBox = logoSize + 14

  return (
    <View style={{position: 'relative'}}>
      <QRCode
        data={link}
        style={[
          a.rounded_sm,
          {height: size, width: size, backgroundColor: '#f3f3f3'},
        ]}
        pieceSize={IS_WEB ? 8 : 6}
        padding={20}
        pieceBorderRadius={IS_WEB ? 4.5 : 3.5}
        outerEyesOptions={{
          topLeft: {
            borderRadius: [12, 12, 0, 12],
            color: t.palette.primary_500,
          },
          topRight: {
            borderRadius: [12, 12, 12, 0],
            color: t.palette.primary_500,
          },
          bottomLeft: {
            borderRadius: [12, 0, 12, 12],
            color: t.palette.primary_500,
          },
        }}
        innerEyesOptions={{borderRadius: 3}}
      />
      {/* The Taderok mark is centered over the QR code itself instead of
          relying on the QRCode `logo` prop, which rendered the Bluesky
          butterfly PNG (assets/logo.png) next to the mark. A plain SVG
          overlay renders reliably on web and native. */}
      <View
        pointerEvents="none"
        style={[
          a.absolute,
          a.align_center,
          a.justify_center,
          {left: 0, right: 0, top: 0, bottom: 0},
        ]}>
        <View
          style={[
            a.align_center,
            a.justify_center,
            {
              width: logoBox,
              height: logoBox,
              borderRadius: 12,
              backgroundColor: 'white',
            },
          ]}>
          <Logo width={logoSize} />
        </View>
      </View>
    </View>
  )
}

/*
 * `react-native-qrcode-styled` sizes a code by its modules, not by its box, so
 * the module size is derived from the module count the library reports
 * (`onChangeSize`) to make the drawn box exactly `size` (see `./qrSizing`).
 * Until the count is known the code is kept invisible rather than drawn at a
 * guessed scale.
 */
function PlainQrCode({link, size}: {link: string; size: number}) {
  const [modules, setModules] = useState<number | null>(null)
  const pieceSize = modules == null ? 0 : qrModuleSize(modules, size)

  return (
    <View style={{width: size, height: size}}>
      <QRCode
        data={link}
        onChangeSize={setModules}
        style={[
          a.rounded_sm,
          {height: size, width: size, backgroundColor: '#ffffff'},
          modules == null && {opacity: 0},
        ]}
        padding={pieceSize * QR_QUIET_ZONE_MODULES}
        pieceSize={pieceSize}
        pieceBorderRadius={0}
        errorCorrectionLevel="L"
        color="#000000"
      />
    </View>
  )
}
